/**
 * space/space-world.js — the room the personal space lives in (GDD 9.1).
 *
 * The room is built once at a far corner of the ground plane and hidden until
 * the player steps through the hub portal, at which point the hub is hidden and
 * the player is blinked across. Keeping both loaded means entering and leaving
 * costs a teleport rather than a scene rebuild.
 *
 * The hub's outdoor lighting rig is switched off while you are inside and a
 * warm interior rig takes over — a directional sun leaking through the ceiling
 * is what makes an interior read as a film set rather than a room.
 *
 * One class, two instances: your space and the read-only copy used to show a
 * visitor's (or the NPC's) collection.
 *
 * @module space/space-world
 */

import * as THREE from 'three';
import { SPACE, ROOM_THEMES, PALETTE } from '../constants.js';
import * as factory from '../assets3d/factory.js';

const HALF = SPACE.roomSize / 2;
/** Where the exit portal sits along the entrance wall. */
const EXIT_X = 2.6;

/** @param {string} id @returns {object} The theme record, falling back to the default. */
export function roomTheme(id) {
  return ROOM_THEMES.find((t) => t.id === id) || ROOM_THEMES[0];
}

/**
 * The four walls, as single-sided planes facing the middle of the room.
 *
 * This is the dollhouse trick, and it is load-bearing rather than decorative:
 * the third-person camera trails the goose by 3.6 m (AVATAR.followOffset), so
 * in a room only 8 m across it spends most of its time *outside* the shell.
 * Solid walls put the lens inside a slab and the screen goes flat brown. A
 * plane whose normal points inward is lit correctly from within and is
 * backface-culled from without, so the camera simply sees past it.
 *
 * @returns {Array<{x:number, z:number, w:number, ry:number}>}
 */
function wallSpans() {
  return [
    { x: 0, z: -HALF, w: SPACE.roomSize, ry: 0 },
    { x: 0, z: HALF, w: SPACE.roomSize, ry: Math.PI },
    { x: -HALF, z: 0, w: SPACE.roomSize, ry: Math.PI / 2 },
    { x: HALF, z: 0, w: SPACE.roomSize, ry: -Math.PI / 2 },
  ];
}

/**
 * Where a shelf unit may stand: three bays down the right wall, two down the
 * left. Unit 0 and 1 exist from the start; 2–4 unlock with Stars.
 * @returns {Array<{x:number, z:number, ry:number}>}
 */
function shelfBays() {
  return [
    { x: HALF - 0.32, z: -2.35, ry: -Math.PI / 2 },
    { x: HALF - 0.32, z: 0, ry: -Math.PI / 2 },
    { x: HALF - 0.32, z: 2.35, ry: -Math.PI / 2 },
    { x: -HALF + 0.32, z: 2.35, ry: Math.PI / 2 },
    { x: -HALF + 0.32, z: 0, ry: Math.PI / 2 },
  ];
}

/**
 * A cosy 8×8 m trophy room.
 *
 * Everything is authored in room-local space with the origin at the centre of
 * the floor, +Z toward the fish-tank wall and −Z toward the exit portal.
 */
export class SpaceRoom {
  /**
   * @param {{ origin: {x:number,y:number,z:number}, name: string,
   *           themeId?: string, title?: string }} opts
   */
  constructor(opts) {
    this.name = opts.name;
    this.origin = opts.origin;
    this.themeId = opts.themeId || ROOM_THEMES[0].id;

    this.root = new THREE.Group();
    this.root.name = `space-room-${this.name}`;
    this.root.position.set(opts.origin.x, opts.origin.y || 0, opts.origin.z);
    this.root.visible = false;

    /** Named empties other modules hang their content on. @type {Record<string, THREE.Object3D>} */
    this.anchors = {};
    /** Materials the theme restyles. */
    this._skin = { floor: null, wall: null, trim: null, lights: [] };
    this._title = opts.title || 'YOUR SPACE';
    this._titleSign = null;
    this._built = false;
  }

  /** @param {THREE.Scene} scene */
  build(scene) {
    if (this._built) return this;
    this._built = true;
    scene.add(this.root);

    const t = roomTheme(this.themeId);
    this._skin.floor = new THREE.MeshStandardMaterial({ color: t.floor, roughness: 0.92 });
    this._skin.wall = new THREE.MeshStandardMaterial({ color: t.wall, roughness: 0.96 });
    this._skin.trim = new THREE.MeshStandardMaterial({ color: t.trim, roughness: 0.6, metalness: 0.2 });

    this._buildShell();
    this._buildLighting(t);
    this._buildDressing();
    this._buildAnchors();
    this.applyTheme(this.themeId);
    return this;
  }

  // ── Construction ──────────────────────────────────────────────────────────

  /** @private */
  _buildShell() {
    const floor = new THREE.Mesh(
      new THREE.BoxGeometry(SPACE.roomSize + 0.4, 0.2, SPACE.roomSize + 0.4),
      this._skin.floor,
    );
    floor.position.y = -0.1;
    floor.receiveShadow = true;
    floor.name = 'space-floor';
    this.root.add(floor);

    const ceiling = new THREE.Mesh(
      new THREE.PlaneGeometry(SPACE.roomSize, SPACE.roomSize), this._skin.wall,
    );
    ceiling.rotation.x = Math.PI / 2;      // normal points down, into the room
    ceiling.position.y = SPACE.wallHeight;
    ceiling.name = 'space-ceiling';
    this.root.add(ceiling);

    for (const s of wallSpans()) {
      const wall = new THREE.Mesh(
        new THREE.PlaneGeometry(s.w, SPACE.wallHeight), this._skin.wall,
      );
      wall.position.set(s.x, SPACE.wallHeight / 2, s.z);
      wall.rotation.y = s.ry;
      wall.receiveShadow = true;
      this.root.add(wall);

      // Skirting board and picture rail — cheap, and they stop each wall
      // reading as one untextured plane.
      this._addTrim(s, 0.11, 0.24);
      this._addTrim(s, 1.95, 0.08);
    }

    const rug = new THREE.Mesh(
      new THREE.CircleGeometry(1.85, 32),
      new THREE.MeshStandardMaterial({ color: 0xb3423a, roughness: 1 }),
    );
    rug.rotation.x = -Math.PI / 2;
    rug.position.y = 0.014;
    rug.receiveShadow = true;
    this.root.add(rug);

    const rugInner = new THREE.Mesh(
      new THREE.RingGeometry(1.2, 1.42, 32),
      new THREE.MeshStandardMaterial({ color: 0xf0d9a8, roughness: 1 }),
    );
    rugInner.rotation.x = -Math.PI / 2;
    rugInner.position.y = 0.02;
    this.root.add(rugInner);
  }

  /**
   * A band of trim along one wall, sitting a whisker proud of it so it does not
   * z-fight, and inheriting the wall's inward facing.
   * @param {{x:number, z:number, w:number, ry:number}} span
   * @param {number} y @param {number} height
   * @private
   */
  _addTrim(span, y, height) {
    const trim = new THREE.Mesh(
      new THREE.PlaneGeometry(span.w * 0.998, height), this._skin.trim,
    );
    trim.rotation.y = span.ry;
    trim.position.set(
      span.x - Math.sin(span.ry) * 0.008, y, span.z - Math.cos(span.ry) * 0.008,
    );
    this.root.add(trim);
  }

  /** @private */
  _buildLighting(t) {
    const key = new THREE.PointLight(t.light, 14, 13, 2);
    key.position.set(0, 2.75, 0);
    key.name = 'space-key-light';
    this.root.add(key);
    this._skin.lights.push(key);

    // A cooler bounce from the tank wall and a warm one from the shelf wall,
    // so the two halves of the room do not read as the same flat surface.
    const tankFill = new THREE.PointLight(0x7fd4ff, 7, 9, 2);
    tankFill.position.set(0, 1.9, 3.0);
    this.root.add(tankFill);

    const shelfFill = new THREE.PointLight(t.light, 6, 9, 2);
    shelfFill.position.set(3.0, 2.1, 0);
    this.root.add(shelfFill);
    this._skin.lights.push(shelfFill);

    const amb = new THREE.AmbientLight(0xffffff, 0.55);
    this.root.add(amb);

    // The shade the key light hangs in.
    const shade = new THREE.Mesh(
      new THREE.ConeGeometry(0.42, 0.34, 18, 1, true),
      new THREE.MeshStandardMaterial({
        color: 0xf6e0b8, roughness: 0.8, side: THREE.DoubleSide,
        emissive: 0x4a3a1c, emissiveIntensity: 1,
      }),
    );
    shade.position.set(0, 2.92, 0);
    this.root.add(shade);
    const flex = new THREE.Mesh(
      new THREE.CylinderGeometry(0.012, 0.012, 0.3, 6),
      this._skin.trim,
    );
    flex.position.set(0, 3.14, 0);
    this.root.add(flex);
  }

  /** @private */
  _buildDressing() {
    // Bunting across the ceiling — two strings of alternating pennants.
    for (let s = 0; s < 2; s++) {
      const string = new THREE.Group();
      const count = 11;
      for (let i = 0; i < count; i++) {
        const k = i / (count - 1);
        const pennant = new THREE.Mesh(
          new THREE.ConeGeometry(0.11, 0.24, 3),
          new THREE.MeshStandardMaterial({
            color: i % 2 ? PALETTE.stripeA : PALETTE.ticket, roughness: 0.9,
            side: THREE.DoubleSide,
          }),
        );
        pennant.rotation.x = Math.PI;
        pennant.position.set(
          -HALF + 0.4 + k * (SPACE.roomSize - 0.8),
          SPACE.wallHeight - 0.28 - Math.sin(k * Math.PI) * 0.26,
          0,
        );
        string.add(pennant);
      }
      string.position.z = s === 0 ? -2.3 : 2.3;
      this.root.add(string);
    }

    // The way home, in the corner of the entrance wall rather than the middle
    // of it: the follow camera sits on that centre line as you arrive, and a
    // 1.7 m portal disc filling the establishing shot is not the first thing
    // anybody should see of their own trophy room.
    const portal = factory.createPortal(0x6fe7c4, 0.7);
    portal.position.set(EXIT_X, 0, -HALF + 0.22);
    portal.name = 'space-exit-portal';
    this.root.add(portal);
    this.anchors.exitPortal = portal;

    const exitSign = factory.createSign('', {
      w: 1.5, h: 0.38, lines: ['BACK TO THE MIDWAY'], size: 40,
    });
    exitSign.position.set(EXIT_X, 1.95, -HALF + 0.2);
    this.root.add(exitSign);

    this._titleSign = factory.createSign('', {
      w: 2.6, h: 0.52, lines: [this._title], size: 62,
    });
    this._titleSign.position.set(0, 2.62, HALF - 0.14);
    this._titleSign.rotation.y = Math.PI;
    this.root.add(this._titleSign);

    // A potted plant so the corner is not dead space.
    const pot = new THREE.Mesh(
      new THREE.CylinderGeometry(0.17, 0.13, 0.28, 12),
      new THREE.MeshStandardMaterial({ color: 0xa8613a, roughness: 0.9 }),
    );
    pot.position.set(-3.3, 0.14, 3.3);
    this.root.add(pot);
    for (let i = 0; i < 5; i++) {
      const leaf = new THREE.Mesh(
        new THREE.ConeGeometry(0.07, 0.62, 5),
        new THREE.MeshStandardMaterial({ color: 0x4f8f4a, roughness: 0.85 }),
      );
      leaf.position.set(-3.3 + Math.cos(i * 1.3) * 0.09, 0.56, 3.3 + Math.sin(i * 1.3) * 0.09);
      leaf.rotation.z = Math.cos(i * 1.3) * 0.32;
      leaf.rotation.x = -Math.sin(i * 1.3) * 0.32;
      this.root.add(leaf);
    }
  }

  /** @private */
  _buildAnchors() {
    const add = (key, x, y, z, ry = 0) => {
      const o = new THREE.Object3D();
      o.position.set(x, y, z);
      o.rotation.y = ry;
      o.name = `anchor-${key}`;
      this.root.add(o);
      this.anchors[key] = o;
      return o;
    };

    add('tank', 0, 0, HALF - 0.85, Math.PI);
    add('guestbook', -2.9, 0, -HALF + 1.1, Math.PI * 0.25);
    add('board', -HALF + 0.2, 1.55, -1.4, Math.PI * 0.5);
    add('centre', 0, 0, 0);

    this.anchors.shelfBays = shelfBays().map((b, i) => add(`shelf-bay-${i}`, b.x, 0, b.z, b.ry));

    // Wall slots decorations hang on.
    this.anchors.roomDecor = [
      add('decor-room-0', -HALF + 0.2, 2.5, 2.4, Math.PI * 0.5),
      add('decor-room-1', 0, 2.45, HALF - 0.22, Math.PI),
      add('decor-room-2', HALF - 0.2, 2.5, -3.1, -Math.PI * 0.5),
    ];
  }

  // ── Theming ───────────────────────────────────────────────────────────────

  /** @param {string} themeId */
  applyTheme(themeId) {
    const t = roomTheme(themeId);
    this.themeId = t.id;
    this._skin.floor.color.setHex(t.floor);
    this._skin.wall.color.setHex(t.wall);
    this._skin.trim.color.setHex(t.trim);
    for (const l of this._skin.lights) l.color.setHex(t.light);
    return t;
  }

  /** @param {string} text */
  setTitle(text) {
    this._title = text;
    this._titleSign?.userData.setText([text]);
  }

  // ── Placement helpers ─────────────────────────────────────────────────────

  /**
   * @param {number} x @param {number} y @param {number} z
   * @returns {THREE.Vector3} The world position of a room-local point.
   */
  toWorld(x, y, z) {
    return new THREE.Vector3(x, y, z).add(this.root.position);
  }

  /**
   * Keep the goose off the walls. The avatar has no collider, so the room's
   * bounds are enforced by clamping rather than by physics.
   * @param {THREE.Vector3} worldPos Mutated in place.
   */
  clampToRoom(worldPos) {
    const limit = HALF - 0.55;
    worldPos.x = THREE.MathUtils.clamp(worldPos.x, this.root.position.x - limit, this.root.position.x + limit);
    worldPos.z = THREE.MathUtils.clamp(worldPos.z, this.root.position.z - limit, this.root.position.z + limit);
  }

  /** @returns {THREE.Vector3} World position of the way out. */
  exitPosition() {
    return this.toWorld(EXIT_X, 0, -HALF + 0.22);
  }

  /** @param {boolean} on */
  setVisible(on) {
    this.root.visible = on;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// The hub's outdoor rig has to stand down while you are indoors.
// ─────────────────────────────────────────────────────────────────────────────

const OUTDOOR_LIGHTS = ['hemi-light', 'sun', 'fill'];
let _stashed = null;

/**
 * Switch the hub's sun/hemisphere/fill off (or back on).
 * @param {THREE.Scene} scene
 * @param {boolean} indoors
 */
export function setIndoorLighting(scene, indoors) {
  if (!scene) return;
  if (indoors) {
    if (_stashed) return;
    _stashed = [];
    for (const name of OUTDOOR_LIGHTS) {
      const l = scene.getObjectByName(name);
      if (l) { _stashed.push([l, l.visible]); l.visible = false; }
    }
    return;
  }
  if (!_stashed) return;
  for (const [l, was] of _stashed) l.visible = was;
  _stashed = null;
}

/**
 * Where a VR player may stand inside the room. Comfort is teleport-only
 * (GDD 12.3), so everything worth looking at needs a node in front of it.
 * @returns {Array<{id: string, x: number, z: number, yaw: number}>}
 */
export function roomNodeLayout() {
  return [
    { id: 'space-centre', x: 0, z: 0, yaw: Math.PI },
    { id: 'space-shelves', x: 2.3, z: 0, yaw: Math.PI / 2 },
    // Close to the glass: the follow camera trails 3.6 m, so standing further
    // back puts the tank in the middle distance rather than in front of you.
    { id: 'space-tank', x: 0, z: 2.6, yaw: 0 },
    { id: 'space-guestbook', x: -2.0, z: -2.1, yaw: -Math.PI * 0.75 },
  ];
}

/**
 * A neighbour's door beside your own, so visiting somebody exists as a feature
 * for a player with nobody else online.
 * @param {THREE.Object3D} parent Parented to the hub so it hides with it.
 * @param {number} x @param {number} z
 */
export function buildNeighbourDoor(parent, x, z) {
  const portal = factory.createPortal(0x43b5c0, 1.1);
  portal.position.set(x, 0, z);
  portal.name = 'gustav-space-portal';
  parent.add(portal);

  // A sign's readable face is its local +Z, which is the side of the hub the
  // player approaches from — turned 180° it showed its opaque backing board.
  // z + 0.16 clears the ring: the portal's torus reaches z + 0.12.
  const sign = factory.createSign('', { w: 1.9, h: 0.5, lines: ["GUSTAV'S SPACE"], size: 44 });
  sign.position.set(x, 2.7, z + 0.16);
  parent.add(sign);
  return portal;
}
